import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Package, Upload, X, FileText, Image as ImageIcon, Film, Presentation, Palette } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Button, Badge, SearchInput, EmptyState, Card } from "@/components/ui-em";

const ICONS = {
  PDF: FileText,
  Images: ImageIcon,
  Videos: Film,
  Presentations: Presentation,
  "Brand Assets": Palette,
  "Marketing Material": Package,
};

function bytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(1)} ${u[i]}`;
}

export default function AdminDownloads() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "PDF", meta: null });
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api
      .get("/downloads", { params: { q: q || undefined, category: category !== "all" ? category : undefined, raw: 1 } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/downloads/categories").then((r) => setCategories(r.data));
  }, []);
  useEffect(load, [q, category]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, title: f.title || file.name, meta: r.data }));
      toast.success("File uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.meta) return toast.error("Upload a file first");
    try {
      await api.post("/downloads", {
        title: form.title,
        description: form.description,
        category: form.category,
        file_path: form.meta.path,
        file_size: form.meta.size,
        content_type: form.meta.content_type,
        original_filename: form.meta.original_filename,
      });
      toast.success("Added to Downloads");
      setShowForm(false);
      setForm({ title: "", description: "", category: "PDF", meta: null });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await api.delete(`/downloads/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin · Content"
        title="Downloads Management"
        description="Upload, categorize and manage every downloadable asset."
        actions={<Button icon={Upload} onClick={() => setShowForm(true)} size="lg" data-testid="admin-new-download">Upload file</Button>}
      />

      <div className="flex flex-col md:flex-row gap-3">
        <SearchInput value={q} onChange={setQ} placeholder="Search files..." className="flex-1" />
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="em-input md:w-56">
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-zinc-500">Loading...</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No files uploaded"
            description="Upload documents, brand assets and marketing material for your team."
            actionLabel="Upload first file"
            onAction={() => setShowForm(true)}
            className="m-4"
          />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold text-left">
                <th className="px-6 py-4">File</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Size</th>
                <th className="px-6 py-4">Added</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const Icon = ICONS[item.category] || FileText;
                return (
                  <tr key={item.id} className="border-t border-white/5 hover:bg-white/[0.02]" style={{ transitionProperty: "background-color", transitionDuration: "140ms" }}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4 text-gold" strokeWidth={1.75} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-white truncate">{item.title}</div>
                          <div className="text-xs text-zinc-500 truncate">{item.original_filename}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4"><Badge variant="outline">{item.category}</Badge></td>
                    <td className="px-6 py-4 text-xs text-zinc-400">{bytes(item.file_size)}</td>
                    <td className="px-6 py-4 text-xs text-zinc-500">{new Date(item.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => remove(item.id)}
                        className="p-2 rounded-md text-zinc-500 hover:text-red-300 hover:bg-red-500/10"
                        style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-ink-0/70 backdrop-blur-md">
          <div className="em-card p-8 w-full max-w-lg" style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)" }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-xl">Upload file</h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <input ref={fileRef} type="file" onChange={onFile} className="hidden" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full em-input flex items-center justify-center gap-2 py-6 border-dashed hover:border-gold cursor-pointer disabled:opacity-50" style={{ transitionProperty: "border-color", transitionDuration: "180ms" }}>
                <Upload className="w-4 h-4 text-gold" />
                <span className="text-sm">{uploading ? "Uploading..." : form.meta ? form.meta.original_filename : "Click to select a file"}</span>
              </button>
              <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="em-input" />
              <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="em-input" rows={3} />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="em-input">
                {categories.map((c) => <option key={c}>{c}</option>)}
              </select>
              <Button type="submit" variant="primary" size="lg" className="w-full" disabled={uploading || !form.meta}>Add to Downloads</Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
